/**
 * Cart store: items del carrito en construcción, persistido en sessionStorage.
 *
 * El cálculo de totales (subtotal, IGV, total) se hace con selectores derivados,
 * usando la convención de precios GROSS del backend.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import type { ProductOut } from "@/types/api.ts";

const DEFAULT_IGV_RATE = 0.18;

export interface CartLine {
  productId: string;
  product: ProductOut;
  quantity: number;
}

interface CartState {
  lines: CartLine[];
  addProduct: (product: ProductOut, qty?: number) => void;
  setQuantity: (productId: string, qty: number) => void;
  removeProduct: (productId: string) => void;
  clear: () => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      lines: [],

      addProduct: (product, qty = 1) =>
        set((state) => {
          const existing = state.lines.find((l) => l.productId === product.id);
          if (existing) {
            return {
              lines: state.lines.map((l) =>
                l.productId === product.id ? { ...l, quantity: l.quantity + qty } : l,
              ),
            };
          }
          return {
            lines: [...state.lines, { productId: product.id, product, quantity: qty }],
          };
        }),

      setQuantity: (productId, qty) =>
        set((state) => ({
          lines:
            qty <= 0
              ? state.lines.filter((l) => l.productId !== productId)
              : state.lines.map((l) => (l.productId === productId ? { ...l, quantity: qty } : l)),
        })),

      removeProduct: (productId) =>
        set((state) => ({ lines: state.lines.filter((l) => l.productId !== productId) })),

      clear: () => set({ lines: [] }),
    }),
    {
      name: "sistema-sale-pos.cart",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

// ---------- Selectores derivados (totales del carrito) ----------------------

export interface CartTotals {
  subtotal: number;
  igvAmount: number;
  total: number;
  itemCount: number;
}

const ROUND = (n: number): number => Math.round(n * 100) / 100;

function lineGross(line: CartLine): number {
  return ROUND(Number(line.product.base_price) * line.quantity);
}

function lineIgvAmount(line: CartLine): number {
  if (line.product.igv_exempt) return 0;
  const rate = line.product.igv_rate != null ? Number(line.product.igv_rate) : DEFAULT_IGV_RATE;
  const gross = lineGross(line);
  const net = ROUND(gross / (1 + rate));
  return ROUND(gross - net);
}

export function computeTotals(lines: CartLine[]): CartTotals {
  let total = 0;
  let igvAmount = 0;
  let itemCount = 0;
  for (const line of lines) {
    total += lineGross(line);
    igvAmount += lineIgvAmount(line);
    itemCount += line.quantity;
  }
  total = ROUND(total);
  igvAmount = ROUND(igvAmount);
  return {
    subtotal: ROUND(total - igvAmount),
    igvAmount,
    total,
    itemCount,
  };
}
